import { Iterable, List } from 'immutable';

import { RowIsFocusable } from 'flow/components/nav/list-keys/list-key-action';
import { CallRecord } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ExtensionVoiceMessage } from 'flow/types/VoiceMessage';
import { DateRows } from './DateGroupedList';
import { type CallRecordOrVoiceMessageRecord, type DateGroups } from './types';

export type CallRecordRow = {
    kind: 'callRecord';
    callRecordKey: CallRecordKey;
};

export type CallHistoryRow =
    | CallRecordRow
    | { kind: 'voiceMessage'; voiceMessage: TypedMap<ExtensionVoiceMessage> };

export const callRecordRow = (callRecordKey: CallRecordKey): CallRecordRow => ({
    kind: 'callRecord',
    callRecordKey,
});

/** The keys of finished calls by date, newest first. */
export type CallRecordKeysByDate = Iterable<string, List<CallRecordKey>>;

// Each date with its calls, without the call to leave out, such as the call
// whose page is open. A date with no call left is left out too.
export const callRecordKeyDates = (
    callRecordKeysByDate: CallRecordKeysByDate | undefined,
    excludeCallRecordKey?: CallRecordKey,
) => {
    const dates: DateRows<CallRecordRow>[] = [];
    callRecordKeysByDate?.forEach((callRecordKeys, date) => {
        const rows = callRecordKeys
            .filter((callRecordKey) => callRecordKey !== excludeCallRecordKey)
            .toArray()
            .map(callRecordRow);
        if (rows.length > 0) {
            dates.push({ date, rows });
        }
    });
    return dates;
};

const callHistoryRow = (
    record: CallRecordOrVoiceMessageRecord,
): CallHistoryRow =>
    (record as TypedMap<ExtensionVoiceMessage>).has('voiceMessageState')
        ? {
              kind: 'voiceMessage',
              voiceMessage: record as TypedMap<ExtensionVoiceMessage>,
          }
        : callRecordRow((record as TypedMap<CallRecord>).get('key'));

// Each date with its calls or voicemails.
export const callHistoryDates = (dateGroups: DateGroups | null) => {
    const dates: DateRows<CallHistoryRow>[] = [];
    dateGroups?.forEach((records, date) => {
        dates.push({
            date,
            rows: records.valueSeq().map(callHistoryRow).toArray(),
        });
    });
    return dates;
};

// React keys each row element by this key, and the Tab stop names its row by
// it. So the element, its focus and the Tab stop follow one call.
export const callHistoryRowKey = (row: CallHistoryRow) =>
    row.kind === 'callRecord'
        ? row.callRecordKey
        : row.voiceMessage.get('uniqueId');

// Whether the row opens the page of this call record. A voicemail without a
// call record opens no page.
export const rowOpensCallRecord = (
    row: CallHistoryRow,
    callRecordKey: CallRecordKey | undefined,
) => {
    const rowCallRecordKey =
        row.kind === 'callRecord'
            ? row.callRecordKey
            : row.voiceMessage.get('call');
    return callRecordKey !== undefined && rowCallRecordKey === callRecordKey;
};

export const rowIsFocusable: RowIsFocusable<CallHistoryRow> = () => true;
